import exifr from "exifr";

export type MediaMetadata = Record<string, string | number>;

export type MediaLocation = {
  latitude: number;
  longitude: number;
};

export type MetadataRow = {
  key: string;
  label: string;
  value: string;
};

type MetadataInput = Parameters<typeof exifr.gps>[0];

type ExifrFileProbe = {
  getString: (offset: number, length: number) => string;
  getUint16: (offset: number) => number;
};

type ExifrFileParser = {
  canHandle: (file: ExifrFileProbe, firstTwoBytes: number) => boolean;
  upgalleryHeicPatched?: boolean;
};

const preferredOrder = [
  "Title",
  "Artist",
  "Album",
  "AlbumArtist",
  "Track",
  "Disc",
  "Genre",
  "Date",
  "Resolution",
  "DateTimeOriginal",
  "Make",
  "Model",
  "LensModel",
  "Software",
  "Format",
  "Duration",
  "__videoStreams",
  "__audioStreams",
  "Subtitles",
  "ExposureTime",
  "FNumber",
  "ISO",
  "FocalLength",
  "GPSLatitude",
  "GPSLongitude",
  "GPSAltitude",
  "GPSHorizontalAccuracy",
];

const labels: Record<string, string> = {
  Album: "Album",
  AlbumArtist: "Album artist",
  Artist: "Artist",
  Date: "Date",
  DateTimeOriginal: "Captured",
  Duration: "Duration",
  ExposureTime: "Exposure time",
  FNumber: "Aperture",
  FocalLength: "Focal length",
  Format: "Format",
  GPSAltitude: "Altitude",
  GPSHorizontalAccuracy: "Location accuracy",
  GPSLatitude: "Latitude",
  GPSLongitude: "Longitude",
  ISO: "ISO",
  LensModel: "Lens",
  Make: "Make",
  Model: "Model",
  Disc: "Disc",
  Genre: "Genre",
  Resolution: "Resolution",
  Rotation: "Rotation",
  Software: "Software",
  Subtitles: "Subtitles",
  Title: "Title",
  Track: "Track",
};

const videoStreamFields: readonly string[] = [
  "Resolution",
  "Codec",
  "Language",
  "BitRate",
  "FrameRate",
  "BitDepth",
  "Rotation",
];

const audioStreamFields: readonly string[] = [
  "Codec",
  "Language",
  "BitRate",
  "SampleRate",
  "Channels",
  "BitDepth",
];

type StreamMetadataKey = {
  kind: "Video" | "Audio";
  track?: number;
  field: string;
};

const streamFieldLabels: Record<string, string> = {
  BitDepth: "bit depth",
  BitRate: "bitrate",
  Channels: "channels",
  Codec: "codec",
  FrameRate: "frame rate",
  Language: "language",
  Resolution: "resolution",
  Rotation: "rotation",
  SampleRate: "sample rate",
};

export function parseMetadataJson(json: string): MediaMetadata | null {
  try {
    const parsed: unknown = JSON.parse(json);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    const metadata: MediaMetadata = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (
        (typeof value === "string" && value.trim() !== "") ||
        (typeof value === "number" && Number.isFinite(value))
      ) {
        metadata[key] = value;
      }
    }
    return Object.keys(metadata).length === 0 ? null : metadata;
  } catch {
    return null;
  }
}

export function metadataRows(
  metadata: MediaMetadata,
  uploader?: string,
): MetadataRow[] {
  const rank = new Map(preferredOrder.map((key, index) => [key, index]));
  const rows = Object.entries(metadata)
    .sort(
      ([left], [right]) =>
        metadataRank(left, rank) - metadataRank(right, rank) ||
        left.localeCompare(right),
    )
    .map(([key, value]) => ({
      key,
      label: metadataLabel(key),
      value: formatValue(key, value),
    }));
  return uploader === undefined
    ? rows
    : [{ key: "__uploader", label: "Uploader", value: uploader }, ...rows];
}

export function metadataLocation(
  metadata: MediaMetadata,
): MediaLocation | null {
  const latitude = numericValue(metadata.GPSLatitude);
  const longitude = numericValue(metadata.GPSLongitude);
  if (
    latitude === undefined ||
    longitude === undefined ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }
  return { latitude, longitude };
}

export function openStreetMapUrls(location: MediaLocation): {
  embed: string;
  full: string;
} {
  const latitude = decimalCoordinate(location.latitude);
  const longitude = decimalCoordinate(location.longitude);
  const latitudeDelta = 0.006;
  const longitudeDelta =
    latitudeDelta /
    Math.max(0.2, Math.cos((location.latitude * Math.PI) / 180));
  const bbox = [
    Math.max(-180, location.longitude - longitudeDelta),
    Math.max(-90, location.latitude - latitudeDelta),
    Math.min(180, location.longitude + longitudeDelta),
    Math.min(90, location.latitude + latitudeDelta),
  ]
    .map(decimalCoordinate)
    .join(",");
  const query = new URLSearchParams({
    bbox,
    layer: "mapnik",
    marker: `${latitude},${longitude}`,
  });
  return {
    embed: `https://www.openstreetmap.org/export/embed.html?${query}`,
    full:
      `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}` +
      `#map=16/${latitude}/${longitude}`,
  };
}

export async function fileHasLocationMetadata(
  file: MetadataInput,
): Promise<boolean> {
  installModernHeicDetection();
  const location = await exifr.gps(file).catch(() => undefined);
  return (
    location !== undefined &&
    Number.isFinite(location.latitude) &&
    location.latitude >= -90 &&
    location.latitude <= 90 &&
    Number.isFinite(location.longitude) &&
    location.longitude >= -180 &&
    location.longitude <= 180
  );
}

function formatValue(key: string, value: string | number): string {
  if (typeof value === "string") return value;
  const streamField = parseStreamMetadataKey(key)?.field;
  switch (streamField ?? key) {
    case "Duration":
      return `${trimmedDecimal(value, 2)} s`;
    case "SampleRate":
      return value >= 1_000
        ? `${trimmedDecimal(value / 1_000, 2)} kHz`
        : `${trimmedDecimal(value, 0)} Hz`;
    case "BitDepth":
      return `${trimmedDecimal(value, 0)}-bit`;
    case "BitRate":
      return value >= 1_000_000
        ? `${trimmedDecimal(value / 1_000_000, 2)} Mb/s`
        : `${trimmedDecimal(value / 1_000, 0)} kb/s`;
    case "ExposureTime":
      return value > 0 && value < 1
        ? `1/${Math.round(1 / value)} s`
        : `${trimmedDecimal(value, 3)} s`;
    case "FNumber":
      return `f/${trimmedDecimal(value, 2)}`;
    case "FocalLength":
      return `${trimmedDecimal(value, 2)} mm`;
    case "FrameRate":
      return `${trimmedDecimal(value, 2)} fps`;
    case "GPSAltitude":
    case "GPSHorizontalAccuracy":
      return `${trimmedDecimal(value, 2)} m`;
    case "GPSLatitude":
    case "GPSLongitude":
      return `${decimalCoordinate(value)}°`;
    case "Rotation":
      return `${trimmedDecimal(value, 1)}°`;
    default:
      return String(value);
  }
}

function metadataRank(key: string, rank: Map<string, number>): number {
  const stream = parseStreamMetadataKey(key);
  if (stream !== null) {
    const anchor =
      rank.get(stream.kind === "Video" ? "__videoStreams" : "__audioStreams") ??
      preferredOrder.length;
    const fields =
      stream.kind === "Video" ? videoStreamFields : audioStreamFields;
    const fieldRank = fields.indexOf(stream.field);
    return (
      anchor * 100_000 +
      ((stream.track ?? 1) - 1) * 100 +
      (fieldRank < 0 ? fields.length : fieldRank)
    );
  }
  return (rank.get(key) ?? preferredOrder.length) * 100_000;
}

function metadataLabel(key: string): string {
  const stream = parseStreamMetadataKey(key);
  if (stream === null) return labels[key] ?? humanizeKey(key);
  const track = stream.track === undefined ? "" : ` ${stream.track}`;
  return `${stream.kind}${track} ${streamFieldLabels[stream.field] ?? humanizeKey(stream.field).toLocaleLowerCase()}`;
}

function parseStreamMetadataKey(key: string): StreamMetadataKey | null {
  const match = key.match(
    /^(Video|Audio)(\d+)?(Resolution|Codec|Language|BitRate|FrameRate|SampleRate|Channels|BitDepth|Rotation)$/,
  );
  if (match === null) return null;
  const track = match[2] === undefined ? undefined : Number(match[2]);
  return {
    kind: match[1] as "Video" | "Audio",
    ...(track === undefined ? {} : { track }),
    field: match[3]!,
  };
}

function numericValue(value: string | number | undefined): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function humanizeKey(key: string): string {
  return key
    .replaceAll(/([a-z\d])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/^./, (character) => character.toUpperCase());
}

function decimalCoordinate(value: number): string {
  return value.toFixed(6).replace(/\.?0+$/, "");
}

function trimmedDecimal(value: number, digits: number): string {
  return value.toFixed(digits).replace(/\.?0+$/, "");
}

function installModernHeicDetection(): void {
  const parser = exifr.fileParsers.get("heic") as
    | ExifrFileParser
    | undefined;
  if (parser === undefined || parser.upgalleryHeicPatched === true) return;

  const originalCanHandle = parser.canHandle;
  parser.canHandle = function (
    file: ExifrFileProbe,
    firstTwoBytes: number,
  ): boolean {
    if (originalCanHandle.call(this, file, firstTwoBytes)) return true;
    if (firstTwoBytes !== 0) return false;
    const ftypLength = file.getUint16(2);
    if (ftypLength < 16 || ftypLength > 4096) return false;
    for (let offset = 8; offset + 4 <= ftypLength; offset += 4) {
      if (
        new Set([
          "heic",
          "heix",
          "hevc",
          "hevx",
          "heim",
          "heis",
          "hevm",
          "hevs",
        ]).has(file.getString(offset, 4))
      ) {
        return true;
      }
    }
    return false;
  };
  parser.upgalleryHeicPatched = true;
}
